import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HardHat, GraduationCap, Laptop, ShieldCheck, Stethoscope, Award, Zap, ChevronRight, CheckCircle2, Star, PhoneCall, Sparkles, Users, MapPin, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { categories, courses, stats, testimonials, siteInfo, faqs } from "../data/mock";
import Countdown from "../components/Countdown";
import CourseCard from "../components/CourseCard";
import Partners from "../components/Partners";
import HowItWorks from "../components/HowItWorks";
import News from "../components/News";
import GoogleMap from "../components/GoogleMap";
import GoogleReviews from "../components/GoogleReviews";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const iconMap = { HardHat, GraduationCap, Laptop, ShieldCheck, Stethoscope, Award, Zap };

const Home = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filterableCategories = useMemo(
    () => categories.filter(c => courses.some(course => course.category === c.id)),
    []
  );

  const filteredCourses = useMemo(() => {
    if (activeCategory === "all") return courses;
    return courses.filter(c => c.category === activeCategory);
  }, [activeCategory]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand text-white">
        <div className="absolute inset-0 bg-dots-pattern opacity-40"></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
                Progettiamo insieme il tuo <span className="text-emerald-300 italic">futuro</span> professionale
              </h1>
              <p className="text-lg md:text-xl text-emerald-100/90 leading-relaxed mb-8 max-w-xl">
                Oltre 29 corsi di formazione qualificati, certificazioni EIPASS, DM 37/08, sicurezza sul lavoro e OSS. Docenti esperti, tirocini in strutture reali e pagamenti rateizzabili.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#corsi">
                  <Button size="lg" className="bg-white text-brand hover:bg-emerald-50 rounded-full h-14 px-8 font-bold text-base w-full sm:w-auto shadow-lg">
                    Scopri i corsi <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/richiedi-info">
                  <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full h-14 px-8 font-bold text-base w-full sm:w-auto">
                    <Info className="w-4 h-4 mr-2" /> Richiedi info
                  </Button>
                </Link>
              </div>

              <div className="mt-12 flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-3">
                    {["a","b","c","d"].map((_,i) => (
                      <div key={i} className={`w-9 h-9 rounded-full border-2 border-brand bg-emerald-${300 + i*100} flex items-center justify-center text-xs font-bold text-brand-dark`}>
                        {String.fromCharCode(65 + i)}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <div className="flex items-center gap-1">{[...Array(5)].map((_,i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}</div>
                    <div className="text-emerald-100/80 text-xs">500+ studenti soddisfatti</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="animate-fade-in-up delay-200 lg:pl-8">
              <Countdown />
              <div className="mt-6 grid grid-cols-3 gap-4">
                {stats.slice(0, 3).map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.value}</div>
                    <div className="text-xs text-emerald-200 mt-1 uppercase tracking-wider">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <svg className="w-full h-16 -mb-1" viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,60 C480,10 960,10 1440,60 L1440,60 L0,60 Z" fill="#fafaf7" />
          </svg>
        </div>
      </section>

      {/* PARTNERS / ACCREDITATIONS */}
      <Partners />

      {/* CATEGORIES */}
      <section id="categorie" className="py-24 bg-[#fafaf7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">Aree di formazione</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight">
              Le nostre <span className="italic text-brand">categorie</span>
            </h2>
            <p className="text-lg text-brand-dark/70">Sei aree di specializzazione per offrirti la formazione più completa e riconosciuta del territorio.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat) => {
              const Icon = iconMap[cat.icon] || GraduationCap;
              const hasCourses = courses.some(c => c.category === cat.id);
              const linkTarget = hasCourses ? `#corsi` : `/contatti`;
              return (
                <a href={linkTarget} onClick={() => hasCourses && setActiveCategory(cat.id)} key={cat.id} className="group bg-white rounded-3xl p-8 border border-emerald-100 hover:border-brand hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-brand-light text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                      <Icon className="w-7 h-7" strokeWidth={2} />
                    </div>
                    <Badge variant="outline" className="border-emerald-200 text-brand-light text-xs">{cat.countLabel}</Badge>
                  </div>
                  <h3 className="font-display text-xl font-bold text-brand-dark mb-3">{cat.title}</h3>
                  <p className="text-sm text-brand-dark/70 leading-relaxed mb-5">{cat.description}</p>
                  <div className="flex items-center gap-1 text-sm font-semibold text-brand">
                    Scopri <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ALL COURSES WITH FILTER */}
      <section id="corsi" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">Catalogo corsi</Badge>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight">
                Tutti i nostri <span className="italic text-brand">corsi</span>
              </h2>
              <p className="text-lg text-brand-dark/70">
                {courses.length} percorsi formativi con qualifica finale, tirocini reali e certificazioni riconosciute.
              </p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-10 pb-2 border-b border-emerald-100">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                activeCategory === "all"
                  ? "bg-brand text-white"
                  : "bg-brand-light text-brand hover:bg-emerald-200"
              }`}
            >
              Tutti ({courses.length})
            </button>
            {filterableCategories.map((cat) => {
              const count = courses.filter(c => c.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                    activeCategory === cat.id
                      ? "bg-brand text-white"
                      : "bg-brand-light text-brand hover:bg-emerald-200"
                  }`}
                >
                  {cat.title.split("—")[0].trim()} ({count})
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} />
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <p className="text-center py-16 text-brand-dark/60">Nessun corso in questa categoria.</p>
          )}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-5xl md:text-6xl font-bold text-brand">{s.value}</div>
                <div className="text-sm text-brand-dark/70 mt-2 font-medium uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <HowItWorks />

      {/* WHY US */}
      <section className="py-24 bg-[#fafaf7]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">Perché sceglierci</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
              Una formazione <span className="italic text-brand">che fa la differenza</span>
            </h2>
            <p className="text-lg text-brand-dark/70 mb-8 leading-relaxed">
              Da oltre 15 anni formiamo professionisti nel settore socio-sanitario, della sicurezza, delle certificazioni informatiche e degli impianti. La nostra missione è aprire le porte al tuo futuro.
            </p>

            <div className="space-y-4">
              {[
                { title: "Docenti esperti e qualificati", desc: "Il nostro corpo docente vanta anni di esperienza nel settore." },
                { title: "Qualifiche riconosciute", desc: "Titoli validi sul territorio nazionale ed europeo, DM 37/08 e MIUR." },
                { title: "Tirocini in strutture reali", desc: "Esperienza pratica presso l'Ospedale di Vibo Valentia, scuole ed aziende partner." },
                { title: "Pagamenti rateizzabili", desc: "Formazione accessibile con pagamenti dilazionati in comode rate." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-brand-dark mb-1">{item.title}</h4>
                    <p className="text-sm text-brand-dark/70">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-200 to-emerald-100 rounded-3xl -rotate-2"></div>
            <img src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=800&q=80" alt="Studenti in formazione" className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-2xl" />
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl border border-emerald-100 max-w-xs">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-brand" />
                <div className="font-display font-bold text-brand-dark text-xl">98% Successo</div>
              </div>
              <p className="text-xs text-brand-dark/70">dei nostri studenti trova lavoro entro 6 mesi dal termine del corso</p>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS - real Google Business reviews carousel */}
      <GoogleReviews />

      {/* TEAM section removed */}

      {/* NEWS */}
      <News />

      {/* GOOGLE BUSINESS / MAPS */}
      <GoogleMap />

      {/* FAQ */}
      <section className="py-24 bg-[#fafaf7]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">Domande frequenti</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight">
              Hai <span className="italic text-brand">domande</span>?
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-emerald-100 px-6">
                <AccordionTrigger className="text-left font-bold text-brand-dark hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-brand-dark/70 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-30"></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <Badge className="bg-white/10 hover:bg-white/15 text-emerald-100 border-white/20 mb-6 backdrop-blur-sm">
            <PhoneCall className="w-3 h-3 mr-1.5" /> Contattaci subito
          </Badge>
          <h2 className="font-display text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Pronto a costruire il tuo <span className="italic text-emerald-300">futuro</span>?
          </h2>
          <p className="text-lg md:text-xl text-emerald-100/90 mb-10 max-w-2xl mx-auto">
            Compila il modulo di richiesta informazioni e un nostro operatore ti ricontatterà entro 48 ore con tutti i dettagli sul corso di tuo interesse.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/richiedi-info">
              <Button size="lg" className="bg-white text-brand hover:bg-emerald-50 rounded-full h-14 px-10 font-bold text-base">
                <Info className="w-4 h-4 mr-2" /> Richiedi informazioni <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${siteInfo.phone}`}>
              <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-full h-14 px-10 font-bold text-base">
                <PhoneCall className="w-4 h-4 mr-2" /> {siteInfo.phone}
              </Button>
            </a>
          </div>

          <div className="mt-12 flex items-center justify-center gap-2 text-sm text-emerald-100/80">
            <MapPin className="w-4 h-4" /> {siteInfo.address}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
